// Does this drum have machine traces worth opening?
//
// The Charts button was shown whenever the task had a machineId, and the window
// it opened fell back to the last twelve hours when the drum had no timestamps.
// Put together, those two produce the most convincing kind of wrong screen:
// real traces, read off the real machine, attributed to a drum that was never
// made on it.
//
// Three origins have no trace of their own, for three different reasons:
//
//   simulated     the drum was completed from a desk. The machine was running
//                 something else at that moment — or nothing — and whatever it
//                 was gets drawn under this drum's name.
//   factory sync  backfilled production from before the system recorded it.
//                 There was never a trace. (This one already had no button, but
//                 only because such tasks carry no machineId — an accident, not
//                 a decision, and it stops protecting anything the moment a
//                 backfill is given a machine.)
//   phantom       pre-made stock consumed by this job. It ran on another job's
//                 machine, in another window.
//
// And a fourth case that is not about origin at all: a drum with no start or
// end has no window, so there is nothing to ask InfluxDB for. Twelve hours of
// the machine's life is not this drum's trace.

const SIMULATED = 'simulate_execution';
const FACTORY_SYNC = 'factory_sync';

const reasonOf = (token, task) =>
  String(token?.completedReason || task?.completedReason || '').trim();

/**
 * Where this drum's production came from.
 *
 * @returns 'live' | 'simulated' | 'factory-sync' | 'phantom' | 'unproduced'
 */
export function productionOrigin(token, task) {
  if (task?.isPhantomSource || token?.isPhantom) return 'phantom';

  const reason = reasonOf(token, task);
  if (reason === SIMULATED) return 'simulated';
  if (reason === FACTORY_SYNC) return 'factory-sync';

  const produced = Number(token?.producedLength);
  if (!Number.isFinite(produced) || produced <= 0) return 'unproduced';

  return 'live';
}

export const isSimulatedOrigin = (token, task) =>
  productionOrigin(token, task) === 'simulated';

/** A real, bounded window — not a fallback that means "recently". */
export function traceWindow(token) {
  const start = Number(token?.startedAt);
  const end = Number(token?.completedAt);
  const ok = Number.isFinite(start) && start > 0 && Number.isFinite(end) && end > start;
  return ok ? { startedAt: start, completedAt: end } : null;
}

const WHY = {
  simulated:
    'This drum was completed from a desk, not by the machine. Any trace in its ' +
    'window belongs to whatever the line was really doing.',
  'factory-sync':
    'Backfilled through Factory Sync — produced before the system was recording, ' +
    'so there is no trace to show.',
  phantom: 'Pre-made stock: it was produced on another job, in another window.',
  unproduced: 'Nothing has been wound on this drum yet.'
};

/**
 * Whether to offer the traces, and what to say when not.
 *
 * @returns {{ available, origin, reason, window }} — `reason` is a sentence for
 *          a tooltip, because "no charts" without a why reads as a broken button.
 */
export function machineTraceAvailability({ token, task, machineId } = {}) {
  const origin = productionOrigin(token, task);
  const window = traceWindow(token);

  if (!machineId) {
    return { available: false, origin, window, reason: 'No machine is recorded for this drum.' };
  }
  if (origin !== 'live') {
    return { available: false, origin, window, reason: WHY[origin] };
  }
  if (!window) {
    return {
      available: false,
      origin,
      window,
      reason:
        'This drum has no start and end recorded, so there is no window to read ' +
        'the machine over.'
    };
  }
  return { available: true, origin, window, reason: null };
}
